/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { gql, useMutation } from "@apollo/client";
import "@fullcalendar/core/main.css";
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";
import "primeflex/primeflex.css";
import "primeicons/primeicons.css";
import { Dialog } from "primereact/dialog";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/saga-blue/theme.css";
import { Tooltip } from "primereact/tooltip";
import "prismjs/themes/prism-coy.css";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../App.scss";
import SelectOptionNotice from "../SelectOptionNotice";
import "../layout/flags/flags.css";
import "../layout/layout.scss";
import { removeFromBuiltDomains } from "../reducers/buildReducer";
import { setCommonDomainData } from "../reducers/commonDomainDataReducer";
import { pushMessage } from "../reducers/messagingReducer";
import { registerScreenChanges } from "../reducers/screenChangesReducer";
import { decodeHtml, getIn, isEmpty, noop } from "../utilities/utils";
import LayoutWrapper from "./LayoutWrapper";

import AdSettingsInput from "./helper/AdSettingsInput";
import CInputSwitch from "./helper/CInputSwitch";
import SectionHeader from "./helper/SectionHeader";

import useAdgridToolTipManager from "../customHooks/useAdgridToolTipManager";
import MarketplaceDomainOverview from "./common/marketplace/MarketplaceDomainOverview";
import AdSettingsDropdown from "./helper/AdSettingsDropdown";
import deepcopy from "deepcopy";
import { setNavigationMode } from "../reducers/userLoginReducer";
import { useDomainSettings } from "../hooks/useDomainSettings";
import { useGeneralDomainSettingsSave } from "../hooks/useGeneralDomainSettingsSave";
import { useCommonDomainInfo } from "../hooks/useCommonDomainInfo";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { useHistory } from "react-router";
import Row from "./helper/Row";
import { DOMAIN_TYPES_MUTATION } from "./common/multiBuild/constants";
import { setDomainTypesMappings } from "../reducers/domainTypesReducer";
import {InputTextarea} from "primereact/inputtextarea";

const MARKETPLACE_WELCOME_EMAIL_MUTATION = gql`
    mutation callMarketplaceNotificationMutation($from: String, $to: String, $subject: String, $partner_logo_url: String, $action: String, $customerId: String) {
        marketplaceNotificationMutation(input: { from: $from, to: $to, subject: $subject, partner_logo_url: $partner_logo_url, action: $action, customerId: $customerId }) {
            response
        }
    }
`;

const ADVERTISING_ALIGN_OPTIONS = [
    { code: "center", name: "Center" },
    { code: "left", name: "Left" },
    { code: "right", name: "Right" },
];

const ADVERTISING_LABEL_POSITIONS = [
    { code: "before", name: "Before" },
    { code: "after", name: "After" },
];

const DEFAULT_MARKETPLACE_DATA = {
    marketplace_domain_name: "",
    marketplace_domain_url: "",
    marketplace_impressions: "",
    marketplace_domain_description: "",
    marketplace_domain_logo: "",
    enable_safetag: false,
    marketplace_safetag_words: "",
};

const AdSettings = () => {
    const [presetName, setPresetName] = useState(false);
    const history = useHistory();
    const [mutationDataCompleted, setMutationDataCompleted] = useState(false);
    const [originalSettingsData, setOriginalSettingsData] = useState({})
    const [settingsData, setSettingsData] = useState({});
    const [showSettingsInfo, setShowSettingsInfo] = useState(false);
    const { currentDomain, currentCustomer, userID, customerToDomainsMappings, isAdmin } = useSelector((state) => state.userInformation);
    const dispatch = useDispatch();
    const [marketplaceNetworks, setMarketplaceNetworks] = useState([]);
    const [suggestedMarketplaceNetworks, setSuggestedMarketplaceNetworks] = useState([]);
    const [selectedMarketplaceNetworks, setSelectedMarketplaceNetworks] = useState([]);
    const [marketplaceEnabled, setMarketplaceEnabled] = useState(false);
    const [showToC, setShowToC] = useState(false);
    const commonDomainInfoMutation = useCommonDomainInfo()
    let adgridVersionTypes = [
        { label: "Version 5", value: "v5" },
    ];
    let videoPlayerVendors = [
        { label: "JW Player", value: "jw_player" },
        { label: "Brid.TV Player", value: "brid_player" }
    ]

    const [marketplaceData, setMarketplaceData] = useState(DEFAULT_MARKETPLACE_DATA);
    const [wasMarketplaceInitiallyEnabled, setWasMarketplaceInitiallyEnabled] = useState(false);
    const [marketplaceWelcomeEmailMutation] = useMutation(MARKETPLACE_WELCOME_EMAIL_MUTATION);
    const [marketplaceEmailsList, setMarketplaceEmailsList] = useState("");
    const { toolTipText: helpText, toolTipTextWithAttributes: tooltip } = useAdgridToolTipManager('general_ad_settings');
    const [domainTypeListingMutation] = useMutation(DOMAIN_TYPES_MUTATION)

    const handleChange = (param) => {
        let { target: { name, value } } = param;
        let temp = {};

        switch (name) {
            case "marketplace_network":
                setSelectedMarketplaceNetworks(value);
                break;
            case "enable_marketplace":
                setMarketplaceEnabled(!marketplaceEnabled);
                break;
            case "marketplace_domain_name":
            case "marketplace_domain_url":
            case "marketplace_impressions":
            case "marketplace_domain_description":
            case "marketplace_domain_logo":
            case "marketplace_safetag_words":
            case "display_sizes":
                setMarketplaceData({ ...marketplaceData, [name]: value });
                break;
            case "enable_safetag":
            case "allows_video":
                setMarketplaceData({ ...marketplaceData, [name]: !marketplaceData[name] });
                break;
            case "marketplace_mail_list":
                setMarketplaceEmailsList(value);
                break;
            default:
                temp = Object.assign(temp, settingsData);
                temp[name] = value;

                if (name === "adapter_domain") {
                    if (value) {
                        setMarketplaceEnabled(value)
                    }
                }

                if (name === "disable_hb_globally_by_country") {
                    temp[name] = JSON.stringify(value);
                }

                setSettingsData(temp);
        }
        dispatch(registerScreenChanges("General Ad Settings"));
    };

    useEffect(() => {
        if (currentDomain && !isEmpty(customerToDomainsMappings)) {
            try {
                if (!marketplaceEnabled && !marketplaceData.marketplace_domain_name) {
                    let domainData = customerToDomainsMappings?.[currentCustomer].find(({ value }) => {
                        return value === currentDomain;
                    });
                    let { name } = domainData;
                    let domainName = name.split(".")[0];
                    domainName = domainName[0].toUpperCase() + domainName.slice(1);
                    setMarketplaceData({ ...marketplaceData, marketplace_domain_name: domainName, marketplace_domain_url: "https://" + name });
                }
            } catch {
                noop()
            }
        }
    }, [currentDomain, customerToDomainsMappings, marketplaceData]);

    const DOMAIN_SETTINGS_MUTATION_CONTAINER = useDomainSettings({
        onCompleted(data) {
            let temp_data = JSON.parse(data.domainEditMutation.customerInfo);
            /** @type {['wrapper_domain', 'adapter_domain']} */
            let domainClientType = temp_data.domain_client_type || []
            setMarketplaceEmailsList(temp_data.marketplace_mail_list || "");

            setWasMarketplaceInitiallyEnabled(!!temp_data.enable_marketplace);

            if (domainClientType.length) {
                temp_data.adapter_domain = domainClientType.includes('adapter_domain')
                temp_data.wrapper_domain = domainClientType.includes('wrapper_domain')

                if (!temp_data.adapter_domain && !temp_data.wrapper_domain) {
                    temp_data.wrapper_domain = true
                }

                if (!temp_data.adapter_domain) {
                    setMarketplaceEnabled(!!temp_data.enable_marketplace);
                } else {
                    setMarketplaceEnabled(true)
                    temp_data.enable_marketplace = true
                }


            } else {
                temp_data.wrapper_domain = true
                temp_data.adapter_domain = false
                setMarketplaceEnabled(!!temp_data.enable_marketplace);
            }

            temp_data.video_player_type = temp_data.video_player_type || "jwp_embed";
            temp_data.player_vendor = temp_data.player_vendor || "jw_player";
            temp_data.enable_video_wrapper = typeof temp_data.enable_video_wrapper  === 'string' && temp_data.enable_video_wrapper.length 
                ? !!parseInt(temp_data.enable_video_wrapper)
                : !!temp_data.enable_video_wrapper 

            setSettingsData(temp_data);

            let presetName = getIn(temp_data, "adv_preset", "name");

            if (presetName) {
                setPresetName(presetName);
            }

            setOriginalSettingsData(temp_data);
            dispatch(setNavigationMode('domain'))

            try {

                let mutMarketplaceData = temp_data.marketplace_domain_overview || null
                let marketplaceDomainOverview = typeof mutMarketplaceData === 'object'
                    ? mutMarketplaceData
                    : JSON.parse(mutMarketplaceData)

                setMarketplaceData({ ...marketplaceData, ...marketplaceDomainOverview });

                let marketplaceNetworks = (temp_data?.marketplace_networks ?? []).map((network) => {
                    network.name = decodeHtml(network.name);
                    return network;
                });

                setMarketplaceNetworks(
                    marketplaceNetworks.filter((networkObjectInfo) => {
                        let name = networkObjectInfo?.name?.toLowerCase() || "";
                        return !(name.includes("Run-of-network") || name.includes("run-of-network"));
                    })
                );

                setSelectedMarketplaceNetworks(temp_data?.associated_networks ?? []);
            } catch (err) {
                console.log({ err })
                dispatch(pushMessage("General settings", "Information parse error", "error"))
            }


            setIndustrySuggestion(temp_data.industry_options || []);
            setMutationDataCompleted(true);

        },
    })

    const SAVE_GENERAL_SETTINGS_MUTATION_CONTAINER = useGeneralDomainSettingsSave({
        onCompleted(dataUpdate) {
            confirmUpdate(dataUpdate);
            removeFromBuiltDomains(`${currentDomain}`);
            setSettingsData({ ...settingsData, enable_marketplace: marketplaceEnabled });
        }
    })

    const [industrySuggestion, setIndustrySuggestion] = useState([]);

    const searchIndustries = (event, name) => {
        setTimeout(() => {
            let results = [];
            if (!event.query.trim().length) {
                results = results.concat(name === "current_industry" ? industrySuggestion : marketplaceNetworks);
            } else {
                results = (name === "current_industry" ? industrySuggestion : marketplaceNetworks) || [];
                results = results.filter((items) => items?.name?.includes(event.query));
            }

            setSuggestedMarketplaceNetworks(results);
        }, 150);
    };

    useEffect(() => {
        if (currentDomain && currentCustomer) {
            setShowSettingsInfo(true);
            setMarketplaceData(DEFAULT_MARKETPLACE_DATA);
            DOMAIN_SETTINGS_MUTATION_CONTAINER({ domainId: currentDomain, customerId: currentCustomer });
        }
    }, [currentDomain, currentCustomer, userID]);

    const confirmUpdate = () => {
        dispatch(pushMessage("General Ad Settings", "General settings updated"));
    };

    function curateMarketplaceData(marketplaceData) {
        let requiredMarketplaceFields = [
            ["Domain name", "marketplace_domain_name"],
            ["Marketplace domain url", "marketplace_domain_url"],
        ];

        function replaceDoubleQuotes(str) {
            return str.replace(/"/g, '\\"');
        }

        function jsonEscape(str) {
            return str.replace(/\n/g, "\\\n").replace(/\r/g, "\\\r").replace(/\t/g, "\\\t");
        }

        if (isEmpty(selectedMarketplaceNetworks)) {
            throw new Error("To participate in the Marketplace you must select at least one category for you domain");
        }

        if (!settingsData.adapter_domain && !marketplaceEmailsList.trim()) {
            throw new Error("You must enter at least one email address to receive notifications from the Marketplace");
        }

        for (let [requiredPropertyName, requiredPropertyKey] of requiredMarketplaceFields) {
            let value = marketplaceData[requiredPropertyKey]?.trim();
            if (!value) {
                dispatch(pushMessage("Marketplace domain overview", `${requiredPropertyName} missing`, "error"));
                throw new Error("Error registering marketplace information");
            }
            marketplaceData[requiredPropertyKey] = replaceDoubleQuotes(value);
        }

        if (marketplaceData.marketplace_domain_description) {
            marketplaceData.marketplace_domain_description = jsonEscape(replaceDoubleQuotes((marketplaceData.marketplace_domain_description || "").trim()));
        }

        return marketplaceData
    }

    const saveAdSettings = async () => {
        let marketplaceDataCopy = { ...marketplaceData };

        if (!settingsData.wrapper_domain && !settingsData.adapter_domain) {
            throw new Error("A domain type must be selected");
        }

        if (marketplaceEnabled) {
            curateMarketplaceData(marketplaceDataCopy)
        }

        if (settingsData.wrapper_domain) {
            if (settingsData.player_vendor === 'brid_player') {
                if (!(settingsData.bridPartnerId || '').trim()) {
                    dispatch(pushMessage("Brid.TV settings", "Brid partner ID is required", "error"))
                    throw new Error("Error saving settings")
                }

                if (!(settingsData.bridApiKey || "").trim()) {
                    dispatch(pushMessage("Brid.TV settings", "Brid API key is required", "error"))
                    throw new Error('Error saving Brid.TV settings')
                }
            }

            if (!settingsData.network_id) {
                dispatch(pushMessage("General Ad Settings", "Please enter Network ID", "error"));
                throw new Error("Please enter network id");
            }

            if (!(/\/\d+\/\w+/).test(settingsData.network_id)) {
                dispatch(pushMessage("General Ad Settings", "Network ID must be in format /network_id/ad_unit", "error"))
                throw new Error("Please enter Network ID in correct format");
            }
            if (!settingsData?.adv_preset?.code) {
                dispatch(pushMessage("General Ad Settings", "Please select Adv Preset", "error"));
                throw new Error("Please select Adv Preset");
            }

            if (settingsData.banner_floors_price && isNaN(parseFloat(settingsData.banner_floors_price))) {
                dispatch(pushMessage("General Ad Settings", "Invalid Banner Price Floors value", "error"));
                throw new Error("Invalid Banner Price Floors value");
            }
        }

        await SAVE_GENERAL_SETTINGS_MUTATION_CONTAINER({
            id: String(currentDomain),
            advPreset: String(settingsData.adv_preset.code),
            networkId: settingsData.network_id,
            childnetworkId: settingsData.child_network_id,
            enableCLS: Boolean(settingsData.enable_cls),
            overrideSiteCSS: Boolean(settingsData.override_site_css),
            industryOptions: JSON.stringify(settingsData.current_industry),
            ads_ActiveViewTime: settingsData.ads_active_view_time,
            shortDomainName: settingsData.short_domain_name,

            bannerFloorsPrice: settingsData.banner_floors_price,
            morning_6_12: settingsData.morning_6_12,
            afternoon_12_6: settingsData.afternoon_12_6,
            evening_6_12: settingsData.evening_6_12,
            midnight_12_6: settingsData.midnight_12_6,
            overrideDynamicFloors: settingsData.override_dynamic_floors,
            enableDynamicFloors: settingsData.enable_dynamic_floors,
            dynamicFloorsPercentage: settingsData.dynamic_floors_percentage,

            adEnableSandboxMode: Boolean(settingsData.enable_sandbox_mode),
            sandboxAllowedDomains: settingsData.sandbox_allowed_domains,
            adEnableAdhesionIcon: Boolean(settingsData.enable_adhesion_close_icon),
            adInactive: settingsData.inactive,
            adPrebidConflict: settingsData.prebid_conflict,
            sendAllBids: settingsData.enable_send_all_bids,
            adDisableHBGlobally: settingsData.disable_hb_globally,
            adDisableHB_GloballyByCountries: settingsData.disable_hb_globally_by_country,
            adIframeLazyload: settingsData.iframe_lazyload,
            adEnableVideoWrapper: settingsData.enable_video_wrapper,
            adgVersion: settingsData.adgrid_version || adgridVersionTypes[0].value,
            stagingURL: settingsData.staging_url,
            markdown: settingsData.markdown,
            domainMarketplaceNetworks: selectedMarketplaceNetworks.map((item) => item.term_id || item.code).join(","),
            enable_marketplace: marketplaceEnabled || settingsData.adapter_domain,
            video_player_type: settingsData.player_vendor === "brid_player" ? "jwp_plugin" : "jwp_embed",
            advertisement_label_text: settingsData.advertisement_label_text || "Advertisement",
            advertisement_label_align: settingsData.advertisement_label_align || "center",
            advertisement_label_position: settingsData.advertisement_label_position || "after",
            stickyHeaderHeight: settingsData.sticky_header_height,
            stickyHeaderHeightTab: settingsData.sticky_header_height_tab,
            stickyHeaderHeightMob: settingsData.sticky_header_height_mob,
            marketplaceDomainOverviewData: JSON.stringify({ ...marketplaceDataCopy, selected_networks: selectedMarketplaceNetworks }),
            marketplaceEmailsList: marketplaceEmailsList,
            playerVendor: settingsData.player_vendor,
            bridPartnerId: settingsData.bridPartnerId,
            bridApiKey: settingsData.bridApiKey,
            isAdapterDomain: settingsData.adapter_domain,
            isWrapperDomain: settingsData.wrapper_domain
        });

        let domainTypeDataResponse = await domainTypeListingMutation({ variables: { id: String(currentCustomer) } })
        /** @type {Object<string, DomainTypeData[]>} */
        let data = JSON.parse(domainTypeDataResponse.data?.domainTypeMutation?.domainTypesListing || '{}');

        dispatch(setDomainTypesMappings({ [currentCustomer]: data }))

        let response = await commonDomainInfoMutation({ domainId: currentDomain });

        let domainData = JSON.parse(response.data?.domainCommonInfoMutation?.domainInfo || null);

        if (domainData) {
            dispatch(setCommonDomainData(currentDomain, domainData));
        }

        if (!wasMarketplaceInitiallyEnabled && marketplaceEnabled) {
            await marketplaceWelcomeEmailMutation({
                variables: {
                    subject: "Welcome to the Marketplace",
                    from: "",
                    to: "",
                    partner_logo_url: marketplaceDataCopy.marketplace_domain_logo || "",
                    action: "SEND_WELCOME_EMAIL",
                    customerId: String(currentDomain),
                },
            });
        }

        return {
            originalData: deepcopy(originalSettingsData),
            mutatedData: settingsData,
        }
    };

    const _presetDataShower = (overriderToggle, domainDataField, presetDataField) => {
        let {
            [overriderToggle]: overriderData,
            [domainDataField]: domainData,
            [presetDataField]: presetData
        } = settingsData;

        if (overriderData) {
            return domainData;
        } else {
            return presetData;
        }
    }

    const disabledByPresetData = (overriderToggle) => {

        let overriderData = settingsData[overriderToggle];

        return {
            disabled: !overriderData,
            style: {
                backgroundColor: !overriderData ? '#ccc' : 'unset'
            },
        }
    }

    return (
        <LayoutWrapper showBuildButton={!!settingsData.wrapper_domain} model="domain" saveCallback={async () => {
            return saveAdSettings()

        }}>
            <Dialog visible={showToC} style={{ width: "50vw" }} onHide={() => setShowToC(false)} header="AdGrid Marketplace Terms and Conditions">
                <div className="mb-1">
                    <p>The users that purchase ads on the AdGrid Marketplace are known as the “Buyer”. The domains that sell their inventory on the AdGrid Marketplace are known as the “Seller”.</p>
                    <p>Buyers agree to pay AdGrid the full amount due at the end of each billing period in order to distribute the funds to each Seller in a timely manner.</p>
                    <p>AdGrid charges the Buyer a commission on the final sale of all advertisements placed at the end of each payment period. For normal campaigns this happens at the end of each month, but for large purchase amounts AdGrid has the discretion to charge weekly.</p>
                    <p>
                        On the third of each month, AdGrid will calculate the total amount of the bids delivered within the previous month on the Sellers’ domains. A single check will be issued to the parent Customer within 15 days, along with a report for each of the Seller’s domains that make up
                        the total of the issued check.
                    </p>
                    <p>If a Buyer wants special payment arrangements, such as using an invoice instead of a credit card, each Seller must agree to the payment terms prior to running the ads on the Marketplace.</p>
                    <p>October 1, 2023</p>
                </div>
            </Dialog>

            {!currentDomain || !currentCustomer ? (
                <SelectOptionNotice optionName="customer" />
            ) : (
                <>
                    {mutationDataCompleted && showSettingsInfo && (
                        <div className="p-grid">
                            <div className="p-grid">
                                <div className="small-full-width p-col p-col-content contact-form">
                                    <div className="p-panel p-component">
                                        <SectionHeader screenName="General Settings">
                                            <div className="p-text-bold">General Ad settings</div>
                                        </SectionHeader>
                                        <div className="p-panel-content p-fluid">

                                            {
                                                (!settingsData.adapter_domain || settingsData.wrapper_domain) && (
                                                    <AdSettingsDropdown
                                                        name="adv_preset"
                                                        label="Adv preset"
                                                        value={settingsData?.adv_preset}
                                                        options={settingsData?.adv_preset_options}
                                                        onChange={handleChange}
                                                        optionLabel="name"
                                                        placeholder="Select Adv Preset"
                                                        {...tooltip('adv_preset')}
                                                        required={true}
                                                    />
                                                )
                                            }

                                            {
                                                isAdmin && (
                                                    <Row
                                                        label="Domain Type"
                                                        required={true}
                                                    >
                                                        <div
                                                            css={css`
                                                                display: flex;
                                                                .switch-section {
                                                                    display: flex;
                                                                    align-items: center;
                                                                    margin-right: 1.5em;
                                                                    span {
                                                                        display: block;
                                                                        padding-right: 0.5em;
                                                                    }
                                                                }
                                                            `}
                                                        >
                                                            <div className="switch-section">
                                                                <span>Wrapper Domain</span>
                                                                <InputSwitch
                                                                    name="wrapper_domain"
                                                                    onChange={handleChange}
                                                                    checked={settingsData.wrapper_domain}
                                                                />
                                                            </div>
                                                            <div className="switch-section">
                                                                <span>Adapter Domain</span>
                                                                <InputSwitch
                                                                    name="adapter_domain"
                                                                    onChange={handleChange}
                                                                    checked={settingsData.adapter_domain}
                                                                />
                                                            </div>
                                                        </div>
                                                    </Row>
                                                )
                                            }

                                            <Tooltip target=".adpositiontooltip" />
                                            {
                                                (!settingsData.adapter_domain || settingsData.wrapper_domain) && (
                                                    <>
                                                        <AdSettingsInput labelfor="network_id" label="Network Id" value={settingsData.network_id || ""} changeEvent={handleChange} {...tooltip('network_id')} required={true} />
                                                        <AdSettingsInput labelfor="child_network_id" label="Child Network Id" value={settingsData.child_network_id || ""} changeEvent={handleChange} {...tooltip('child_network_id')} required={false} />
                                                        <AdSettingsInput labelfor="ads_active_view_time" label="Ads Active View Time" display="none" value={settingsData.ads_active_view_time || ""} changeEvent={handleChange} required={false} />

                                                        <AdSettingsInput labelfor="short_domain_name" label="Short Domain Name" value={settingsData.short_domain_name || ""} changeEvent={handleChange} {...tooltip('short_domain_name')} required={false} />

                                                        <AdSettingsInput labelfor="go_live_date" label="Go-live date" value={settingsData.go_live_date || ""} disabled={true} {...tooltip('go-live_date')} required={false} />

                                                        <AdSettingsInput labelfor="staging_url" label="Staging URL" value={settingsData.staging_url || ""} changeEvent={handleChange} {...tooltip('staging_url')} required={false} />

                                                        <AdSettingsDropdown
                                                            {...tooltip('adgrid_version')}
                                                            label="Adgrid Version"
                                                            name="adgrid_version"
                                                            value={settingsData.adgrid_version || adgridVersionTypes[0].value}
                                                            options={adgridVersionTypes}
                                                            onChange={handleChange}
                                                            optionLabel="label"
                                                            placeholder="Select Adgrid Version"
                                                        />
                                                        <AdSettingsDropdown
                                                            {...tooltip('advertisement_label_position')}
                                                            label="Advertisement Label Position"
                                                            name="advertisement_label_position"
                                                            value={settingsData.advertisement_label_position || ADVERTISING_LABEL_POSITIONS[1].code}
                                                            options={ADVERTISING_LABEL_POSITIONS}
                                                            onChange={handleChange}
                                                            optionLabel="name"
                                                            optionValue="code"
                                                            placeholder="Advertisment Label Position"
                                                        />

                                                        <AdSettingsInput
                                                            {...tooltip('advertisement_text')}
                                                            labelfor="advertisement_label_text"
                                                            value={settingsData.advertisement_label_text || ''}
                                                            label="Advertisement Text"
                                                            changeEvent={handleChange}
                                                        />

                                                        <AdSettingsDropdown
                                                            {...tooltip('advertisement_label_align')}
                                                            label="Advertisement Label Align"
                                                            name="advertisement_label_align"
                                                            value={settingsData.advertisement_label_align || ADVERTISING_ALIGN_OPTIONS[0].code}
                                                            options={ADVERTISING_ALIGN_OPTIONS}
                                                            onChange={handleChange}
                                                            optionLabel="name"
                                                            optionValue="code"
                                                            placeholder="Select Advertisement Label Alignment"
                                                        />

                                                        {
                                                            isAdmin && <AdSettingsInput labelfor="markdown" label="Markdown" value={settingsData.markdown || ""} changeEvent={handleChange} required={false} />
                                                        }

                                                        <div className="p-grid small-margin-bottom-10">
                                                            <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                                                Price Floors
                                                            </div>
                                                            <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                                                <div className="mb-1">
                                                                    <div className="p-panel p-component mb-1">
                                                                        <div className="p-datatable p-component p-datatable-gridlines">
                                                                            <div className="p-datatable-header border-bottom-1 p-d-flex p-jc-between p-ai-center">
                                                                                <div className="p-d-flex p-ai-center f-width">

                                                                                    <InputSwitch
                                                                                        id="override_dynamic_floors"
                                                                                        name="override_dynamic_floors"
                                                                                        checked={settingsData.override_dynamic_floors}
                                                                                        onChange={handleChange}
                                                                                    />

                                                                                    <label>
                                                                                        <strong> &nbsp; Override Price Floors &nbsp;
                                                                                            <span
                                                                                                style={{
                                                                                                    paddingBottom: "2px",
                                                                                                    color: "#0388e5",
                                                                                                    cursor: "pointer",
                                                                                                }}
                                                                                                onClick={() => {
                                                                                                    history.push("/presetslotplacementid");
                                                                                                }}
                                                                                            >
                                                                                                [{presetName && ` ${presetName} `}]
                                                                                            </span>
                                                                                        </strong>
                                                                                    </label>
                                                                                </div>
                                                                            </div>
                                                                            <div className="p-panel p-component">
                                                                                <div className="p-panel-content">


                                                                                    <div className="p-grid">
                                                                                        <div className="p-col-4 p-sm-none small-hide">
                                                                                            <label className={"overrideDisableHBGlobally === disable_hb ?" + "text-black" + "gray"}>
                                                                                                Global Banner Price Floors
                                                                                            </label>
                                                                                        </div>
                                                                                        <div className="p-col-12 p-sm-12 p-md-8 p-lg-8 p-fluid">
                                                                                            <div className="p-inputgroup p-fluid">
                                                                                                <InputText
                                                                                                    id="banner_floors_price"
                                                                                                    name="banner_floors_price"
                                                                                                    value={settingsData.banner_floors_price}
                                                                                                    onChange={handleChange}
                                                                                                    placeholder=""
                                                                                                    disabled={false}
                                                                                                    style={{
                                                                                                        backgroundColor: false ? '#ccc' : 'unset'
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>


                                                                                    <div className="adapter-parameters adpos" style={{ paddingRight: 0 }}>
                                                                                        <div className="sizing-row all-sizes"></div>
                                                                                        <div className="controls-wrapper all-sizes" style={{ width: "100%" }}>
                                                                                            <div className="adapter-config">

                                                                                                <div className="device desktop price-floors-heading">
                                                                                                    <small className=""><b>Morning</b> ( 6AM-12PM )</small>
                                                                                                </div>
                                                                                                <div className="device desktop price-floors-heading">
                                                                                                    <small className=""><b>Afternoon</b> ( 12PM-6PM )</small>
                                                                                                </div>
                                                                                                <div className="device desktop price-floors-heading">
                                                                                                    <small className=""><b>Evening</b> ( 6PM-12AM )</small>
                                                                                                </div>
                                                                                                <div className="device desktop price-floors-heading">
                                                                                                    <small className=""><b>Midnight</b> ( 12AM-6PM )</small>
                                                                                                </div>

                                                                                            </div>


                                                                                            <div className="values display-ad">
                                                                                                <div className="adapter-parameter-values price-floors-inputs" data-index="0" style={{ padding: "1rem", width: "25%" }}>

                                                                                                    <div className="param-input adpos">
                                                                                                        <input name="morning_6_12" className="p-inputtext p-component p-filled" value={
                                                                                                            _presetDataShower(
                                                                                                                'override_dynamic_floors',
                                                                                                                'morning_6_12',
                                                                                                                '_preset_morning_6_12'
                                                                                                            )
                                                                                                        }
                                                                                                            onChange={handleChange}
                                                                                                            {...disabledByPresetData('override_dynamic_floors')}
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="adapter-parameter-values price-floors-inputs" data-index="1">

                                                                                                    <div className="param-input adpos">
                                                                                                        <input name="afternoon_12_6" className="p-inputtext p-component p-filled" value={
                                                                                                            _presetDataShower(
                                                                                                                'override_dynamic_floors',
                                                                                                                'afternoon_12_6',
                                                                                                                '_preset_afternoon_12_6'
                                                                                                            )
                                                                                                        }
                                                                                                            onChange={handleChange}
                                                                                                            {...disabledByPresetData('override_dynamic_floors')}
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>

                                                                                                <div className="adapter-parameter-values price-floors-inputs" data-index="2">

                                                                                                    <div className="param-input adpos">
                                                                                                        <input name="evening_6_12" className="p-inputtext p-component p-filled" value={
                                                                                                            _presetDataShower(
                                                                                                                'override_dynamic_floors',
                                                                                                                'evening_6_12',
                                                                                                                '_preset_evening_6_12'
                                                                                                            )
                                                                                                        }
                                                                                                            onChange={handleChange}
                                                                                                            {...disabledByPresetData('override_dynamic_floors')}
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>

                                                                                                <div className="adapter-parameter-values price-floors-inputs" data-index="2">

                                                                                                    <div className="param-input adpos">
                                                                                                        <input name="midnight_12_6" className="p-inputtext p-component p-filled" value={
                                                                                                            _presetDataShower(
                                                                                                                'override_dynamic_floors',
                                                                                                                'midnight_12_6',
                                                                                                                '_preset_midnight_12_6'
                                                                                                            )
                                                                                                        }
                                                                                                            onChange={handleChange}
                                                                                                            {
                                                                                                            ...disabledByPresetData('override_dynamic_floors')
                                                                                                            }
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>

                                                                                            </div>

                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="p-grid">
                                                                                        <div className="p-col-4 p-sm-none small-hide">
                                                                                            <label>
                                                                                                Enable Price Floors
                                                                                            </label>
                                                                                        </div>
                                                                                        <div className="p-col-12 p-sm-12 p-md-8 p-lg-8 p-fluid">
                                                                                            <div className="p-inputgroup p-fluid">
                                                                                                <InputSwitch
                                                                                                    id="enable_dynamic_floors"
                                                                                                    name="enable_dynamic_floors"
                                                                                                    checked={
                                                                                                        _presetDataShower(
                                                                                                            'override_dynamic_floors',
                                                                                                            'enable_dynamic_floors',
                                                                                                            '_preset_enable_dynamic_floors'
                                                                                                        )
                                                                                                    }
                                                                                                    onChange={handleChange}
                                                                                                    disabled={!settingsData.override_dynamic_floors}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>


                                                                                    <div className="p-grid">
                                                                                        <div className="p-col-4 p-sm-none small-hide">
                                                                                            <label className={"overrideDisableHBGlobally === disable_hb ?" + "text-black" + "gray"}>
                                                                                                Dynamic Floors Percentage
                                                                                            </label>
                                                                                        </div>
                                                                                        <div className="p-col-12 p-sm-12 p-md-8 p-lg-8 p-fluid">
                                                                                            <div className="p-inputgroup p-fluid">
                                                                                                <InputText
                                                                                                    id="dynamic_floors_percentage"
                                                                                                    name="dynamic_floors_percentage"
                                                                                                    value={
                                                                                                        _presetDataShower(
                                                                                                            'override_dynamic_floors',
                                                                                                            'dynamic_floors_percentage',
                                                                                                            '_preset_dynamic_floors_percentage'
                                                                                                        )
                                                                                                    }
                                                                                                    // value={settingsData.dynamic_floors_percentage}
                                                                                                    onChange={handleChange}
                                                                                                    placeholder=""
                                                                                                    {
                                                                                                    ...disabledByPresetData('override_dynamic_floors')
                                                                                                    }
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>

                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                </div>

                                                            </div>
                                                        </div>


                                                        <div className="p-grid small-margin-bottom-10">
                                                            <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0 no-pad-left">
                                                                <CInputSwitch name="enable_sandbox_mode" checked={settingsData.enable_sandbox_mode} changeEvent={handleChange} label_text="Enable Sandbox Mode" {...tooltip('enable_sandbox_mode')} />
                                                            </div>
                                                            {settingsData.enable_sandbox_mode &&
                                                            <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                                                <div className="mb-1">
                                                                    <div className="p-panel p-component mb-1">
                                                                        <div className="p-datatable p-component p-datatable-gridlines">
                                                                            <div className="p-datatable-header border-bottom-1 p-d-flex p-jc-between p-ai-center">
                                                                                <div className="p-d-flex p-ai-center f-width">
                                                                                    <label>
                                                                                        <b>Sandbox Allowed Domains</b>
                                                                                    </label>
                                                                                </div>
                                                                            </div>

                                                                            <div className="p-panel p-component">
                                                                                <div className="p-panel-content" style={{padding: 0}}>
                                                                                    <div className="adapter-parameters adpos no-pad-right" style={{padding: 0, margin: 0}}>
                                                                                        <div className="controls-wrapper all-sizes" style={{ width: "100%" }}>
                                                                                            <InputTextarea
                                                                                                rows={10}
                                                                                                cols={30}
                                                                                                name="sandbox_allowed_domains"
                                                                                                style={{width: "100%", border: "none"}}
                                                                                                value={settingsData.sandbox_allowed_domains}
                                                                                                onChange={handleChange}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>}
                                                        </div>


                                                        <div className="p-grid p-mt-2">
                                                            <CInputSwitch name="enable_cls" checked={settingsData.enable_cls} changeEvent={handleChange} label_text="Enable CLS" {...tooltip('enable_cls')} />
                                                        </div>

                                                        {settingsData.enable_cls &&
                                                            <>
                                                                <Tooltip target=".adpositiontooltip" />
                                                                <div class="border-solid border-primary-500">
                                                                    <div className="p-grid p-mt-2">
                                                                        <CInputSwitch name="override_site_css" checked={settingsData.override_site_css} changeEvent={handleChange} label_text="Override Site CSS" {...tooltip('override_site_css')} />
                                                                        {settingsData.override_site_css && <span className="small-notice" style={{ marginBottom: '10px' }}>It may break some styling as it will override the template's CSS.</span>}
                                                                    </div>

                                                                    <div className="adapter-config">
                                                                        <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                                                            <label>Sticky Header Height</label>
                                                                        </div>
                                                                        <AdSettingsInput placeholder="Desktop" labelfor="sticky_header_height" value={settingsData.sticky_header_height || ""} changeEvent={handleChange} required={false} />
                                                                        <AdSettingsInput placeholder="Tablet" labelfor="sticky_header_height_tab" value={settingsData.sticky_header_height_tab || ""} changeEvent={handleChange} required={false} />
                                                                        <AdSettingsInput placeholder="Mobile" labelfor="sticky_header_height_mob" value={settingsData.sticky_header_height_mob || ""} changeEvent={handleChange} required={false} />
                                                                    </div>
                                                                </div>
                                                            </>
                                                        }
                                                        <div className="p-grid p-mt-2">
                                                            <CInputSwitch name="enable_adhesion_close_icon" checked={settingsData.enable_adhesion_close_icon} changeEvent={handleChange} label_text="Enable Adhesion's Close Icon" {...tooltip('enable_adhesions_close_icon')} />

                                                            <CInputSwitch name="inactive" checked={settingsData.inactive} changeEvent={handleChange} label_text="Inactive" {...tooltip('inactive')} />

                                                            <CInputSwitch name="enable_send_all_bids" checked={settingsData.enable_send_all_bids} changeEvent={handleChange} label_text="Send all Prebid bidders to GAM" {...tooltip('send_all_prebid_bidders_to_gam')} />


                                                            <CInputSwitch name="enable_video_wrapper" checked={settingsData.enable_video_wrapper} changeEvent={handleChange} label_text="Enable Video Wrapper" {...tooltip('enable_video_wrapper')} />

                                                            {
                                                                settingsData.enable_video_wrapper && (
                                                                    <div className="small-full-width p-col p-col-content p-col-12">
                                                                        <div className="p-panel p-component">
                                                                            <div className="p-fluid">
                                                                                <AdSettingsDropdown
                                                                                    label="Player Vendor"
                                                                                    name="player_vendor"
                                                                                    value={settingsData.player_vendor}
                                                                                    options={videoPlayerVendors}
                                                                                    onChange={handleChange}
                                                                                    optionLabel="label"
                                                                                    placeholder="Select Video Player"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            }

                                                            {
                                                                settingsData.player_vendor === 'brid_player' &&
                                                                <>
                                                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                                                        <label>
                                                                            Brid Partner ID <span style={{ color: '#ff0000' }}>*</span>
                                                                        </label>
                                                                    </div>
                                                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                                                        <InputText
                                                                            value={settingsData.bridPartnerId || ''}
                                                                            name="bridPartnerId"
                                                                            onChange={handleChange}
                                                                        />
                                                                    </div>

                                                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                                                        <label>
                                                                            Brid API Key <span style={{ color: '#ff0000' }}>*</span>
                                                                        </label>
                                                                    </div>
                                                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                                                        <InputText
                                                                            value={settingsData.bridApiKey || ''}
                                                                            name="bridApiKey"
                                                                            onChange={handleChange}
                                                                        />
                                                                    </div>
                                                                </>
                                                            }

                                                            <CInputSwitch
                                                                name="enable_marketplace"
                                                                checked={marketplaceEnabled || settingsData.adapter_domain}
                                                                changeEvent={handleChange}
                                                                label_text=" "
                                                                disabled={settingsData.adapter_domain}
                                                            >
                                                                <p>
                                                                    I agree to participate in the AdGrid Marketplace and adhere to the{" "}
                                                                    <strong>
                                                                        <span
                                                                            onClick={() => setShowToC(true)}
                                                                            style={{
                                                                                color: "#2196f3",
                                                                                cursor: "pointer",
                                                                                textDecoration: "underline",
                                                                            }}
                                                                        >
                                                                            AdGrid Marketplace Terms and Conditions
                                                                        </span>
                                                                    </strong>
                                                                    . This service allows AdGrid Customers to bid on our ad inventory through the Google Ad Manager{" "}
                                                                    <b>
                                                                        <a href="https://support.google.com/admanager/answer/3721872" target="_blank" rel="noreferrer">
                                                                            dynamic allocation
                                                                        </a>
                                                                    </b>{" "}
                                                                    process.
                                                                </p>
                                                            </CInputSwitch>
                                                        </div>
                                                    </>
                                                )
                                            }
                                        </div>

                                        {(marketplaceEnabled || settingsData.adapter_domain) && (
                                            <div className="p-panel p-component" style={{ marginTop: "1em" }}>
                                                <SectionHeader screenName="Domain Overview">
                                                    <div className="p-text-bold">Domain Overview</div>
                                                </SectionHeader>
                                                <div className="p-panel-content p-fluid">
                                                    <MarketplaceDomainOverview
                                                        currentCustomer={currentCustomer}
                                                        selectedMarketplaceNetworks={selectedMarketplaceNetworks}
                                                        onChange={handleChange}
                                                        suggestedMarketplaceNetworks={suggestedMarketplaceNetworks}
                                                        searchIndustries={searchIndustries}
                                                        marketplaceDomainName={marketplaceData.marketplace_domain_name}
                                                        marketplaceDomainUrl={marketplaceData.marketplace_domain_url}
                                                        marketplaceImpressions={marketplaceData.marketplace_impressions}
                                                        marketplaceDomainDescription={marketplaceData.marketplace_domain_description}
                                                        marketplaceLogo={marketplaceData.marketplace_domain_logo}
                                                        allowSafetag={marketplaceData.enable_safetag}
                                                        safetagWords={marketplaceData.marketplace_safetag_words}
                                                        selectedDisplaySizes={marketplaceData.display_sizes}
                                                        marketplaceMailList={marketplaceEmailsList}
                                                        allowVideo={marketplaceData.allows_video}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {
                                    (!settingsData.adapter_domain || settingsData.wrapper_domain) && (
                                        <>

                                            <div className="small-full-width p-col p-col-sidebar">
                                                <div className="p-panel p-component">
                                                    <SectionHeader title="Descriptions" />
                                                    <div className="p-panel-content p-fluid">
                                                        <h6 className="p-text-bold p-mb-0">Network Id</h6>
                                                        <p>{helpText('help_text____network_id')}</p>
                                                        <h6 className="p-text-bold p-mb-0">Short Domain Name</h6>
                                                        <p>{helpText('help_text____short_domain_name')}</p>
                                                        <h6 className="p-text-bold p-mb-0">Enable Sandbox Mode</h6>
                                                        <p>{helpText('help_text____enable_sandbox_mode')}</p>
                                                        <h6 className="p-text-bold p-mb-0">Enable CLS</h6>
                                                        <p>{helpText('help_text____enable_cls')}
                                                        </p><h6 className="p-text-bold p-mb-0">Override Site CSS</h6>
                                                        <p>{helpText('help_text____override_site_css')}</p>
                                                        <h6 className="p-text-bold p-mb-0 p-d-none">Enable Adhesion's Close Icon</h6>
                                                        <p className="p-d-none">{helpText('')}</p>
                                                        <h6 className="p-text-bold p-mb-0">Inactive</h6>
                                                        <p>{helpText('help_text____inactive')}</p>
                                                        <h6 className="p-text-bold p-mb-0">Disable HB Globally</h6>
                                                        <p>{helpText('help_text____disable_hb_globally')}</p>
                                                        <h6 className="p-text-bold p-mb-0">Sticky Header Height Position</h6>
                                                        <p>{helpText('help_text____sticky_header_height_position')}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )
                                }
                            </div>
                        </div>
                    )}
                </>
            )}
        </LayoutWrapper>
    );
};

export default AdSettings;
